/**
 * The pages under /about, in reading order, with the lookups the router, the
 * landing page, and the tests use. The reading page is built here from the
 * copy the hints already link to, so moving it onto the shared component
 * changes none of its ids or words.
 */
import type { BuildItem } from '../../../src/serve/api.js';
import { COPY } from '../copy.js';
import { buildDate } from '../format.js';
import { headlineSentence } from '../summary.js';
import { aboutPage } from './pages/about.js';
import { coveragePage } from './pages/coverage.js';
import { identifiersPage } from './pages/identifiers.js';
import { mapPage } from './pages/map.js';
import { resolutionPage } from './pages/resolution.js';
import { sourcesPage } from './pages/sources.js';
import { sponsorsPage } from './pages/sponsors.js';
import { termsPage } from './pages/terms.js';
import { verifyPage } from './pages/verify.js';
import type { Block, DocPage, Inline } from './types.js';

export const readingPage: DocPage = {
  slug: 'reading',
  title: COPY.reading.title,
  audience: 'everyone',
  promise: 'How a filing becomes the facts on a page, and what the relationships between organizations mean.',
  lead: 'What a page shows, where each fact comes from, and what the relationships between organizations mean.',
  sections: COPY.reading.sections.map((section) => ({ id: section.id, heading: section.heading, blocks: [{ kind: 'paragraph', text: [section.body] }] })),
};

// Reading order: what the site is, how it reads a filing, what it holds and where from, how names become organizations,
// the sponsors, what the map computes, how to verify and cite a fact, what an identifier names, and the terms.
export const docPages: readonly DocPage[] = [aboutPage, readingPage, coveragePage, sourcesPage, resolutionPage, sponsorsPage, mapPage, verifyPage, identifiersPage, termsPage];

export const docPage = (slug: string): DocPage | undefined => docPages.find((page) => page.slug === slug);

export const nextPage = (slug: string): DocPage | undefined => docPages[docPages.findIndex((page) => page.slug === slug) + 1];

/** The counts are the build's, read at render time; a page never writes a number that the next build changes. */
export const holdsSentence = (build: BuildItem | null): string => {
  // Undefined from the offline server, null from the live schema for a build item older than the field: both mean no counts.
  const headline = build?.headline ?? null;
  return build !== null && headline !== null ? `${headlineSentence(headline)} Built ${buildDate(build.goldRunId)}.` : 'The counts of the current build are on the home page.';
};

const inlineLinks = (parts: readonly Inline[]): string[] => parts.flatMap((part) => (typeof part === 'string' ? [] : [part.link]));

const blockLinks = (block: Block): string[] => {
  switch (block.kind) {
    case 'paragraph':
    case 'note':
      return inlineLinks(block.text);
    case 'list':
      return block.items.flatMap(inlineLinks);
    case 'table':
      return block.rows.flatMap((row) => row.flatMap(inlineLinks));
    case 'headline':
    case 'pages':
      return [];
  }
};

export const docLinks = (page: DocPage): string[] => page.sections.flatMap((section) => section.blocks.flatMap(blockLinks));
